import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  createdAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts = new BehaviorSubject<Toast[]>([]);
  private defaultDuration = 5000;

  constructor() {}

  getToasts(): Observable<Toast[]> {
    return this.toasts.asObservable();
  }

  success(title: string, message: string = '', duration?: number): string {
    return this.addToast({
      type: 'success',
      title,
      message,
      duration: duration || this.defaultDuration
    });
  }

  error(title: string, message: string = '', duration?: number): string {
    return this.addToast({
      type: 'error',
      title,
      message,
      duration: duration || this.defaultDuration
    });
  }

  warning(title: string, message: string = '', duration?: number): string {
    return this.addToast({
      type: 'warning',
      title,
      message,
      duration: duration || this.defaultDuration
    });
  }

  info(title: string, message: string = '', duration?: number): string {
    return this.addToast({
      type: 'info',
      title,
      message,
      duration: duration || this.defaultDuration
    });
  }

  remove(id: string): void {
    const currentToasts = this.toasts.getValue();
    const updatedToasts = currentToasts.filter(toast => toast.id !== id);
    this.toasts.next(updatedToasts);
  }

  clear(): void {
    this.toasts.next([]);
  }

  private addToast(toast: Partial<Toast>): string {
    const id = this.generateId();
    const newToast: Toast = {
      id,
      type: toast.type || 'info',
      title: toast.title || '',
      message: toast.message || '',
      duration: toast.duration,
      createdAt: new Date()
    };

    const currentToasts = this.toasts.getValue();
    this.toasts.next([...currentToasts, newToast]);

    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, newToast.duration);
    }

    return id;
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 9);
  }
}
